<template>
  <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <!-- Desktop View -->
    <div class="hidden sm:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-100 text-sm text-left">
        <thead>
          <tr class="bg-gray-50/70 border-b border-gray-200/80 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs w-28">Registro</th>
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs">Fecha y Hora</th>
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs">Usuario</th>
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs">Cantidad Total</th>
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs">Estado</th>
            <th class="py-3.5 px-6 font-bold uppercase tracking-wider text-xs text-right">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr 
            v-for="inventory in inventories?.data || []" 
            :key="inventory.id"
            class="hover:bg-orange-50/30 transition-colors"
          >
            <td class="py-3.5 px-6 whitespace-nowrap">
              <Link :href="`/inventories/${inventory.id}`" class="font-mono font-bold text-gray-900 hover:text-orange-600 transition-colors">
                #{{ inventory.id }}
              </Link>
            </td>
            
            <td class="py-3.5 px-6 whitespace-nowrap text-gray-600 text-xs">
              {{ getDate(inventory.created_at) }}
            </td>

            <td class="py-3.5 px-6 whitespace-nowrap text-gray-900 font-medium">
              {{ getUserName(inventory.user) }}
            </td>

            <td class="py-3.5 px-6 whitespace-nowrap">
              <span class="font-bold text-gray-900">{{ formatNumber(inventory.quantity) }}</span>
              <span class="text-xs text-gray-400 ml-1">unds.</span>
            </td>

            <td class="py-3.5 px-6 whitespace-nowrap">
              <span 
                :class="{
                  'bg-amber-50 text-amber-700 border-amber-200': inventory.status === 'request',
                  'bg-emerald-50 text-emerald-700 border-emerald-200': inventory.status === 'process',
                  'bg-red-50 text-red-700 border-red-200': inventory.status === 'canceled',
                  'bg-gray-100 text-gray-700 border-gray-200': !['request', 'process', 'canceled'].includes(inventory.status)
                }"
                class="px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border items-center gap-1.5"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full" :class="{
                    'bg-amber-500': inventory.status === 'request',
                    'bg-emerald-500': inventory.status === 'process',
                    'bg-red-500': inventory.status === 'canceled',
                    'bg-gray-400': !['request', 'process', 'canceled'].includes(inventory.status)
                  }"
                />
                {{ translateStatus(inventory.status) }}
              </span>
            </td>

            <td class="py-3.5 px-6 whitespace-nowrap text-right">
              <div class="flex items-center justify-end gap-2">
                <!-- Solo se permite anular solicitudes pendientes -->
                <button 
                  v-if="inventory.status === 'request'" 
                  type="button" 
                  class="px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors flex items-center gap-1 cursor-pointer" 
                  title="Anular solicitud de inventario"
                  @click="$emit('annul-inventory', inventory)"
                >
                  <i class="fa fa-ban text-[10px]" />
                  <span>Anular</span>
                </button>

                <Link 
                  :href="`/inventories/${inventory.id}`" 
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 font-medium rounded-lg text-xs transition-colors"
                >
                  <span>Ver Detalle</span>
                  <i class="fa fa-chevron-right text-[10px]" />
                </Link>
              </div>
            </td>
          </tr>

          <!-- Empty State Desktop -->
          <tr v-if="!inventories?.data || inventories.data.length === 0">
            <td colspan="6" class="py-16 text-center text-gray-400">
              <div class="flex flex-col items-center justify-center">
                <div class="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                  <i class="fa fa-boxes text-2xl text-orange-400" />
                </div>
                <h3 class="text-base font-semibold text-gray-800 mb-1">No hay registros de inventario</h3>
                <p class="text-gray-500 text-sm max-w-sm mb-4">
                  {{ hasActiveFilters ? 'No se encontraron registros con los filtros aplicados.' : 'Crea un nuevo registro para comenzar a gestionar el inventario.' }}
                </p>
                <Link 
                  v-if="!hasActiveFilters"
                  href="/inventories/create" 
                  class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold shadow-sm inline-flex items-center gap-1.5 transition-colors"
                >
                  <i class="fa fa-plus" />
                  <span>Crear primer inventario</span>
                </Link>
                <button 
                  v-else
                  type="button" 
                  class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  @click="$emit('reset-filters')"
                >
                  Limpiar filtros
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile View -->
    <div class="sm:hidden divide-y divide-gray-100">
      <div v-for="inventory in inventories?.data || []" :key="inventory.id" class="p-4 hover:bg-gray-50 transition-colors">
        <Link :href="`/inventories/${inventory.id}`" class="block space-y-2.5">
          <div class="flex justify-between items-start">
            <div>
              <span class="font-mono font-bold text-gray-900 text-sm">#{{ inventory.id }}</span>
              <span class="text-xs text-gray-500 ml-2">{{ getDate(inventory.created_at) }}</span>
            </div>
            <span 
              :class="{
                'bg-amber-50 text-amber-700 border-amber-200': inventory.status === 'request',
                'bg-emerald-50 text-emerald-700 border-emerald-200': inventory.status === 'process',
                'bg-red-50 text-red-700 border-red-200': inventory.status === 'canceled',
                'bg-gray-100 text-gray-700 border-gray-200': !['request', 'process', 'canceled'].includes(inventory.status)
              }"
              class="px-2 py-0.5 text-xs font-semibold rounded-full border"
            >
              {{ translateStatus(inventory.status) }}
            </span>
          </div>
          
          <div class="text-xs text-gray-600 space-y-1">
            <p><i class="fa fa-user mr-1 text-gray-400" />{{ getUserName(inventory.user) }}</p>
            <p><i class="fa fa-box mr-1 text-gray-400" />Cantidad Total: <strong class="text-gray-900">{{ formatNumber(inventory.quantity) }}</strong></p>
          </div>

          <div class="pt-2 flex justify-between items-center border-t border-gray-50">
            <button 
              v-if="inventory.status === 'request'" 
              type="button" 
              class="px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-md transition-colors flex items-center gap-1 cursor-pointer" 
              @click.prevent="$emit('annul-inventory', inventory)"
            >
              <i class="fa fa-ban text-[10px]" />
              <span>Anular Solicitud</span>
            </button>
            <div v-else />
            <span class="text-xs text-orange-600 font-semibold flex items-center gap-1">
              <span>Ver detalle</span>
              <i class="fa fa-chevron-right text-[10px]" />
            </span>
          </div>
        </Link>
      </div>

      <!-- Empty State Mobile -->
      <div v-if="!inventories?.data || inventories.data.length === 0" class="p-8 text-center text-gray-400">
        <i class="fa fa-boxes text-3xl mb-2 text-orange-300" />
        <p class="text-sm">No hay registros de inventario</p>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="inventories?.links && inventories.data?.length > 0" class="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
      <Pagination :links="inventories.links" />
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'InventoryTable',

  components: {
    Link,
    Pagination,
  },

  props: {
    inventories: {
      type: Object,
      required: true,
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['annul-inventory', 'reset-filters'],

  methods: {
    getUserName(user) {
      if (!user) return 'N/A'
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }
      return user.name || 'Usuario'
    },

    getDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-VE', { 
        hour12: false,
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    },

    formatNumber(val) {
      const num = parseFloat(val) || 0
      return num.toLocaleString('es-VE', { maximumFractionDigits: 2 })
    },

    translateStatus(status) {
      if (status === 'request') return 'Solicitud'
      if (status === 'canceled') return 'Anulado'
      return 'Procesado'
    },
  },
}
</script>
